import React, { useState, useEffect, useMemo } from 'react';
import { BeakerIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { GasMeterReading } from '../types/gas';
import { gasMeterReadingService } from '../services/gasService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import { useLanguage } from '../contexts/LanguageContext';
import toast from 'react-hot-toast';

const GasReadings: React.FC = () => {
  const { t } = useLanguage();
  const [readings, setReadings] = useState<GasMeterReading[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const loadReadings = async () => {
    setLoading(true);
    try {
      const data = await gasMeterReadingService.getAllGasMeterReadings();
      setReadings(data);
    } catch (error) {
      console.error('Error loading gas readings:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.readings') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReadings();
  }, []);

  const filteredReadings = useMemo(() => {
    if (!searchTerm.trim()) return readings;
    const term = searchTerm.toLowerCase();
    return readings.filter(reading =>
      reading.gasDay.includes(term) ||
      reading.readingDate?.includes(term) ||
      reading.gasPodId?.toLowerCase().includes(term) ||
      reading.customerId?.toLowerCase().includes(term)
    );
  }, [readings, searchTerm]);

  const totalPages = Math.ceil(filteredReadings.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentReadings = filteredReadings.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const getStatusLabel = (status?: string) => {
    switch (status) {
      case 'estimated': return 'Becsült';
      case 'read': return 'Leolvasott';
      case 'corrected': return 'Helyesbített';
      default: return status || '—';
    }
  };

  const getSourceLabel = (source?: string) => {
    switch (source) {
      case 'manual': return 'Kézi';
      case 'mscsons': return 'MSCONS';
      case 'invoice': return 'Számla';
      case 'import': return 'Import';
      default: return source || '—';
    }
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="space-y-4">
      <CommonPageHeader
        title={t('gas.sections.readings')}
        subtitle={t('gas.list.subtitle', { count: readings.length })}
        icon={BeakerIcon}
      />

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('gas.list.search.readings')}
          />
        </div>
        <button
          onClick={loadReadings}
          className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4 mr-2" />
          {t('gas.list.refresh')}
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.reading.gasDay')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">POD</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.reading.meterStatus')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.reading.consumptionM3')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.reading.energyKwh')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.reading.source')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('gas.reading.status')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentReadings.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    {t('gas.list.noResults')}
                  </td>
                </tr>
              ) : (
                currentReadings.map(reading => (
                  <tr key={reading.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">{reading.gasDay}</span>
                      <p className="text-xs text-gray-500">{reading.readingDate}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-xs text-gray-700">{reading.gasPodId}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{reading.meterStatus ?? '—'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{reading.consumptionM3 ?? '—'} m³</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-700">{reading.energyKwh ?? '—'} kWh</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-700">{getSourceLabel(reading.source)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        reading.status === 'read'
                          ? 'bg-blue-100 text-blue-700'
                          : reading.status === 'estimated'
                            ? 'bg-yellow-100 text-yellow-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}>
                        {getStatusLabel(reading.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={filteredReadings.length}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
      />
    </div>
  );
};

export default GasReadings;
